import { useMemo, type ComponentType } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import {
  AlertCircleIcon,
  DiscussionIcon,
  EyeOffIcon,
  FlagIcon,
  HandIcon,
  HeartPulseIcon,
  LayersIcon,
  LockIcon,
  MegaphoneIcon,
  ShieldIcon,
  ShieldAlertIcon,
  StethoscopeIcon,
  TagIcon,
  UsersIcon,
  WarningTriangleIcon,
  type IconProps,
} from '../../components/ui/icons';
import { MorePage } from '../more/MorePage';
import './communityGuidelines.css';

interface GuidelineItem {
  id: string;
  title: string;
  description: string;
  isUrgent?: boolean;
}

interface AdditionalGuidelineItem {
  id: string;
  title: string;
  description: string;
}

const GUIDELINE_ICONS: Record<string, ComponentType<IconProps>> = {
  bullying: AlertCircleIcon,
  dangerousWeightLoss: WarningTriangleIcon,
  unsafeMedicalAdvice: StethoscopeIcon,
  selfHarm: HeartPulseIcon,
  hateAndDiscrimination: UsersIcon,
  sexualContentAndHarassment: ShieldAlertIcon,
  privacyViolations: LockIcon,
  spamAndScams: MegaphoneIcon,
  obsceneContent: EyeOffIcon,
  politicalContent: FlagIcon,
  religiousDisputes: DiscussionIcon,
  unwantedContact: HandIcon,
  promotionAndSolicitation: TagIcon,
  consent: ShieldIcon,
};

export function CommunityGuidelinesContent() {
  const { t } = useTranslation();
  const items = useMemo(() => {
    const value = t('legal.communityGuidelines.items', { returnObjects: true });
    return Array.isArray(value) ? (value as GuidelineItem[]) : [];
  }, [t]);
  const additionalItems = useMemo(() => {
    const value = t('legal.communityGuidelines.additionalGuidelines', {
      returnObjects: true,
    });
    return Array.isArray(value) ? (value as AdditionalGuidelineItem[]) : [];
  }, [t]);
  const conductItems = additionalItems.filter(item => item.id !== 'scope');
  const scopeItem = additionalItems.find(item => item.id === 'scope');
  const categoryItems: GuidelineItem[] = [...items, ...conductItems];

  return (
    <MorePage
      title={t('legal.communityGuidelines.title')}
      backFallbackPath="/create"
    >
      <div className="community-guidelines">
        <article
          className="community-guidelines__surface"
          aria-labelledby="community-guidelines-title"
        >
          <header className="community-guidelines__hero">
            <span className="community-guidelines__hero-icon" aria-hidden>
              <ShieldIcon size={30} />
            </span>
            <div className="community-guidelines__hero-copy">
              <span className="community-guidelines__hero-badge">
                {t('legal.communityGuidelines.heroBadge')}
              </span>
              <h2 id="community-guidelines-title">
                {t('legal.communityGuidelines.headerTitle')}
              </h2>
              <p>{t('legal.communityGuidelines.headerSubtitle')}</p>
            </div>
            <span className="community-guidelines__hero-orbit" aria-hidden />
          </header>

          <section
            className="community-guidelines__section"
            aria-labelledby="community-rules-heading"
          >
            <div className="community-guidelines__section-heading">
              <span className="community-guidelines__eyebrow">
                {t('legal.communityGuidelines.statusHeldForReview')}
              </span>
              <h2 id="community-rules-heading">
                {t('legal.communityGuidelines.additionalTitle')}
              </h2>
              <p>{t('legal.communityGuidelines.columnReasons')}</p>
            </div>

            <ul className="community-guidelines__card-grid">
              {categoryItems.map(item => {
                const ItemIcon = GUIDELINE_ICONS[item.id] ?? ShieldIcon;
                return (
                  <li
                    className={`community-guidelines__card${
                      item.isUrgent ? ' community-guidelines__card--urgent' : ''
                    }`}
                    key={item.id}
                  >
                    <div className="community-guidelines__card-top">
                      <span
                        className={`community-guidelines__card-icon${
                          item.isUrgent ? ' community-guidelines__card-icon--urgent' : ''
                        }`}
                        aria-hidden
                      >
                        <ItemIcon size={21} />
                      </span>
                    </div>
                    <div className="community-guidelines__card-copy">
                      <h3>{item.title}</h3>
                      {item.isUrgent && (
                        <span className="community-guidelines__urgent">
                          {t('legal.communityGuidelines.statusUrgent')}
                        </span>
                      )}
                      <p>{item.description}</p>
                    </div>
                  </li>
                );
              })}
            </ul>

            {scopeItem && (
              <aside className="community-guidelines__scope">
                <span className="community-guidelines__scope-icon" aria-hidden>
                  <LayersIcon size={20} />
                </span>
                <div>
                  <h3>{scopeItem.title}</h3>
                  <p>{scopeItem.description}</p>
                </div>
              </aside>
            )}
          </section>

          <aside className="community-guidelines__report">
            <span className="community-guidelines__report-icon" aria-hidden>
              <FlagIcon size={21} />
            </span>
            <div>
              <h2>{t('legal.communityGuidelines.reportCard.title')}</h2>
              <p>{t('legal.communityGuidelines.reportCard.description')}</p>
            </div>
          </aside>
        </article>
      </div>
    </MorePage>
  );
}
